import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Sparkles, BookOpen, Lightbulb, FileText } from "lucide-react";

interface CustomPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  contentType?: "mcq" | "note";
}

const promptTemplates = {
  mcq: [
    {
      name: "Case-Based Clinical Scenarios",
      prompt: "Generate structured medical content using AI-powered templates. Create clinical case-based questions that present realistic patient scenarios with detailed patient history, physical examination findings, and diagnostic test results. Include age, gender, chief complaint, and require multi-step clinical reasoning to reach the correct answer.",
      category: "Clinical Focus"
    },
    {
      name: "Pathophysiology & Mechanisms",
      prompt: "Focus on underlying pathophysiology, disease mechanisms, and molecular processes. Questions should test deep understanding of how diseases develop, progress, and affect body systems at cellular and molecular levels.",
      category: "Educational Depth"
    },
    {
      name: "Differential Diagnosis Challenges",
      prompt: "Create complex differential diagnosis questions with similar presentations. Include subtle distinguishing clinical features, laboratory findings, and imaging results that help differentiate between conditions.",
      category: "Clinical Skills"
    },
    {
      name: "Evidence-Based Treatment",
      prompt: "Focus on current evidence-based treatment protocols, drug mechanisms of action, contraindications, side effects, and comprehensive patient management strategies including monitoring and follow-up.",
      category: "Therapeutic"
    },
    {
      name: "Emergency Medicine Scenarios",
      prompt: "Create urgent/emergent clinical scenarios requiring immediate decision-making. Include time-sensitive conditions, critical care protocols, and life-saving interventions.",
      category: "Specialty Focus"
    },
    {
      name: "Diagnostic Imaging & Labs",
      prompt: "Generate questions focused on interpreting diagnostic studies including X-rays, CT, MRI, lab values, and other diagnostic tests. Include normal variants and pathological findings.",
      category: "Diagnostic Skills"
    },
    {
      name: "Pharmacology Deep-Dive",
      prompt: "Create comprehensive pharmacology questions covering drug classifications, mechanisms, interactions, pharmacokinetics, and adverse effects. Include therapeutic drug monitoring and dosing considerations.",
      category: "Pharmacology"
    },
    {
      name: "Patient Communication & Ethics",
      prompt: "Focus on patient communication, informed consent, medical ethics, cultural competency, and professional behavior scenarios. Include challenging ethical dilemmas.",
      category: "Professional Skills"
    }
  ],
  note: [
    {
      name: "Comprehensive Study Guide",
      prompt: "Generate structured medical content using AI-powered templates. Structure as a comprehensive study guide with clear hierarchical headings, bullet points, key facts highlighted, learning objectives, and summary boxes for easy review and retention.",
      category: "Format"
    },
    {
      name: "Clinical Application Focus",
      prompt: "Emphasize real-world clinical applications, patient case examples, bedside pearls, and how theoretical knowledge directly applies to patient care and clinical decision-making.",
      category: "Clinical Focus"
    },
    {
      name: "Pathophysiology Explained",
      prompt: "Provide detailed step-by-step explanations of disease mechanisms, pathophysiology cascades, and molecular processes. Include flowcharts, cause-and-effect relationships, and system interactions.",
      category: "Educational Depth"
    },
    {
      name: "Memory Enhancement Tools",
      prompt: "Include mnemonics, acronyms, memory devices, visual analogies, and clever association techniques to help remember complex medical information and improve long-term retention.",
      category: "Learning Tools"
    },
    {
      name: "Quick Reference Cards",
      prompt: "Create concise, pocket-sized reference materials with essential facts, normal values, diagnostic criteria, treatment algorithms, and emergency protocols for quick clinical reference.",
      category: "Format"
    },
    {
      name: "Case-Based Learning",
      prompt: "Structure content around real patient cases with detailed presentations, thought processes, diagnostic workups, and treatment decisions. Include 'teaching moments' and clinical pearls.",
      category: "Clinical Focus"
    },
    {
      name: "Evidence-Based Content",
      prompt: "Focus on current evidence-based guidelines, recent research findings, clinical trial results, and best practice recommendations. Include references to major medical societies and guidelines.",
      category: "Academic Rigor"
    },
    {
      name: "Visual Learning Aids",
      prompt: "Describe content that would benefit from diagrams, flowcharts, anatomical illustrations, or concept maps. Include suggestions for visual representations of complex concepts.",
      category: "Learning Tools"
    },
    {
      name: "Board Exam Preparation",
      prompt: "Structure content specifically for board examination preparation with high-yield facts, commonly tested concepts, exam tips, and areas frequently emphasized in standardized tests.",
      category: "Exam Prep"
    },
    {
      name: "Specialty-Specific Focus",
      prompt: "Tailor content for specific medical specialties with specialty-relevant terminology, unique considerations, specialty-specific guidelines, and practice patterns.",
      category: "Specialty Focus"
    }
  ]
};

export default function CustomPromptInput({ 
  value, 
  onChange, 
  placeholder = "Add custom instructions for AI generation...",
  label = "Custom Prompt",
  contentType = "mcq"
}: CustomPromptInputProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [showTemplates, setShowTemplates] = useState(false);

  const currentTemplates = promptTemplates[contentType];

  const handleTemplateSelect = (templateName: string) => {
    const template = currentTemplates.find(t => t.name === templateName);
    if (template) {
      const newPrompt = value ? `${value}\n\n${template.prompt}` : template.prompt;
      onChange(newPrompt);
      setSelectedTemplate("");
    }
  };

  const clearPrompt = () => {
    onChange("");
  };

  const templatesByCategory = currentTemplates.reduce((acc, template) => {
    if (!acc[template.category]) {
      acc[template.category] = [];
    }
    acc[template.category].push(template);
    return acc;
  }, {} as Record<string, typeof currentTemplates>);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label htmlFor="custom-prompt" className="text-sm font-medium">
          {label}
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowTemplates(!showTemplates)}
            className="text-xs"
          >
            <BookOpen className="h-3 w-3 mr-1" />
            Templates
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearPrompt}
              className="text-xs"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      <Textarea
        id="custom-prompt"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="border-primary/20 resize-none"
      />

      {value && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lightbulb className="h-3 w-3" />
          <span>{value.length} characters</span>
          <Badge variant="secondary" className="text-xs">
            Custom instructions active
          </Badge>
        </div>
      )}

      {showTemplates && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Prompt Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(templatesByCategory).map(([category, templates]) => (
              <div key={category} className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground">{category}</h4>
                <div className="grid gap-2">
                  {templates.map((template) => (
                    <Button
                      key={template.name}
                      variant="outline"
                      size="sm"
                      onClick={() => handleTemplateSelect(template.name)}
                      className="justify-start text-left h-auto p-3"
                    >
                      <div>
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {template.prompt.substring(0, 100)}...
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}